import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, EventEmitter } from "@angular/core";
import { BsModalRef } from "ngx-bootstrap/modal";
import { NgxExtendedPdfViewerService } from "ngx-extended-pdf-viewer";
import * as Sentry from "@sentry/angular";

import { environment } from "src/environments/environment";
import { blobToBase64 } from "src/app/shared/utils/text";
import {
  WAService,
  WAContentType,
} from "src/app/shared/components/send-to-wa/send-to-wa.service";

export type PDFObtainedAction = 'download' | 'print';

export interface ModalPDFViewerConfig {
  src: Blob | string;
  filename: string;
  title: string;
  modalClass?: string;
  waNumbers?: string[];
  waText?: string;
  /** Callback chamado quando o PDF é obtido (download ou impressão) */
  onPdfObtained?: (action: PDFObtainedAction) => void;
}

/**
 * Erros benignos de cancelamento do pdf.js — ocorrem quando o documento/worker
 * é destruído (usuário fecha/navega antes do PDF carregar) com uma operação
 * pendente. NÃO são falhas reais e não devem ser reportados ao Sentry
 * (GESTOR-CLIENT-T4/T5/T6/TQ).
 */
export function isBenignPdfError(message: string | undefined | null): boolean {
  if (!message) {
    return false;
  }
  return /Transport destroyed|Invalid page request|Worker was destroyed|Rendering cancelled|RenderingCancelledException|AbortException/i.test(
    message
  );
}

@Component({
  selector: "app-pdf-viewer",
  templateUrl: "./pdf-viewer.component.html",
  styleUrls: ["./pdf-viewer.component.css"],
  providers: [WAService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  private config: ModalPDFViewerConfig;
  private resolve: any;
  private reject: any;
  private currentObjectUrl: string | null = null; // Adicionar tracking da URL

  public src: Blob | string | null = null;
  public showToolbar: boolean = false;
  // Falha real ao carregar (rede, CORS, URL assinada expirada): sem isto o
  // modal ficava aberto com o visualizador vazio e nenhuma explicação.
  public loadError: string | null = null;
  // Falha de uma ação do usuário (envio por WhatsApp) — também visível.
  public actionError: string | null = null;
  private viewerInitialized: boolean = false;
  private documentLoaded: boolean = false;
  private pdfFullyLoaded: boolean = false;
  private initializationTimeout: any = null;
  private cleanupInProgress: boolean = false;
  private errorRetryAttempted: boolean = false;
  private errorHandlerBound: ((event: PromiseRejectionEvent) => void) | null = null;

  constructor(
    private modal: BsModalRef,
    private waService: WAService,
    private cdr: ChangeDetectorRef,
    private pdfViewerService: NgxExtendedPdfViewerService,
  ) {}

  ngOnInit(): void {
    // Adicionar listener global para erros do PDF.js
    this.setupPdfErrorHandler();
  }

  ngOnDestroy(): void {
    this.cleanupInProgress = true;

    // IMPORTANTE: Limpar src IMEDIATAMENTE para remover o componente do DOM via *ngIf
    // Isso previne que o PDF viewer continue renderizando durante o cleanup
    this.src = null;
    this.showToolbar = false;

    // Detach change detector para prevenir change detection durante cleanup
    // Isso resolve o erro "TrustedTypePolicy callback is no longer runnable"
    try {
      this.cdr.detach();
    } catch (e) {
      console.warn('Error detaching change detector:', e);
    }

    // Limpar timeout de inicialização se existir
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
      this.initializationTimeout = null;
    }

    // Limpar URL de objeto se existir
    if (this.currentObjectUrl) {
      try {
        URL.revokeObjectURL(this.currentObjectUrl);
      } catch (e) {
        console.warn('Error revoking object URL:', e);
      }
      this.currentObjectUrl = null;
    }

    // Resetar estados
    this.viewerInitialized = false;
    this.documentLoaded = false;
    this.pdfFullyLoaded = false;
    this.errorRetryAttempted = false;

    // Remover listener de erro ao destruir
    if (this.errorHandlerBound) {
      window.removeEventListener('unhandledrejection', this.errorHandlerBound);
      this.errorHandlerBound = null;
    }

    // Dar tempo para o PDF.js limpar recursos
    // Este delay ajuda a evitar race conditions em aberturas consecutivas
    setTimeout(() => {
      this.cleanupInProgress = false;
    }, 100);
  }

  setOption(resolve, reject, config: ModalPDFViewerConfig): void {
    this.config = config;
    this.resolve = resolve;
    this.reject = reject;

    // Resetar cleanup flag quando abrindo novo PDF
    this.cleanupInProgress = false;

    // Reattach change detector se estava detached
    try {
      this.cdr.reattach();
    } catch (e) {
      // Já está attached, ignorar
    }

    // Limpar URL anterior se existir
    if (this.currentObjectUrl) {
      try {
        URL.revokeObjectURL(this.currentObjectUrl);
      } catch (e) {
        console.warn('Error revoking previous object URL:', e);
      }
      this.currentObjectUrl = null;
    }

    // Limpar timeout anterior se existir
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
      this.initializationTimeout = null;
    }

    this.viewerInitialized = false;
    this.documentLoaded = false;
    this.pdfFullyLoaded = false;
    this.showToolbar = false;
    this.errorRetryAttempted = false;
    this.src = null; // Limpar src antes de definir novo valor
    this.loadError = null;
    this.actionError = null;

    // Marcar para verificação antes de mudanças assíncronas
    try {
      this.cdr.markForCheck();
    } catch (e) {
      console.warn('Error marking for check in setOption:', e);
    }

    // Aguardar inicialização adequada do viewer
    // Aumentado de 50ms para 150ms para dar tempo ao OverlayManager inicializar
    this.initializationTimeout = setTimeout(() => {
      this.initializePdfSource(config);
    }, 150);
  }

  private initializePdfSource(config: ModalPDFViewerConfig): void {
    // Não inicializar se cleanup está em progresso
    if (this.cleanupInProgress) {
      return;
    }

    try {
      const startTime = Date.now();

      if (typeof config.src === 'string') {
        this.src = config.src;
      } else if (config.src instanceof Blob) {
        const url = URL.createObjectURL(config.src);
        this.currentObjectUrl = url;
        this.src = url;
      } else {
        this.src = '';
      }

      // Log tempo de inicialização para análise
      const initTime = Date.now() - startTime;
      if (initTime > 100) {
        console.warn(`PDF viewer initialization took ${initTime}ms`);
        Sentry.captureMessage('PDF viewer slow initialization', {
          level: 'warning',
          extra: {
            initTime,
            srcType: typeof config.src,
            filename: config.filename
          }
        });
      }

      // Forçar detecção de mudanças com OnPush strategy
      if (!this.cleanupInProgress) {
        try {
          this.cdr.markForCheck();
        } catch (e) {
          console.warn('Error marking for check in initializePdfSource:', e);
        }
      }
    } catch (error) {
      console.error('Error initializing PDF source:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'pdf-viewer',
          action: 'initialize-source'
        },
        extra: {
          filename: config.filename,
          srcType: typeof config.src
        }
      });
    }
  }

  onPageRendered(_event: unknown): void {
    // Não processar eventos se cleanup está em progresso
    if (this.cleanupInProgress) {
      return;
    }

    // Only set showToolbar to true if not already initialized
    if (!this.viewerInitialized) {
      this.showToolbar = true;
      this.viewerInitialized = true;
      this.documentLoaded = true;
      // Marca para verificação de mudanças com OnPush strategy
      try {
        this.cdr.markForCheck();
      } catch (e) {
        console.warn('Error marking for check:', e);
      }
    }
  }

  onPdfLoaded(_event: unknown): void {
    // Não processar eventos se cleanup está em progresso
    if (this.cleanupInProgress) {
      return;
    }

    this.pdfFullyLoaded = true;

    if (!environment.production) {
      console.log('PDF fully loaded');
    }
  }

  onPdfLoadingFailed(event: { message?: string }): void {
    // Não processar eventos se cleanup está em progresso
    if (this.cleanupInProgress) {
      return;
    }

    this.pdfFullyLoaded = false;

    const message = event?.message || 'Unknown error';

    // Cancelamentos benignos do pdf.js: ocorrem quando o usuário fecha/navega
    // antes do PDF carregar (documento/worker destruído com um getPage pendente).
    // Não são falhas reais — não reportar ao Sentry (GESTOR-CLIENT-T4/T5/T6/TQ).
    if (isBenignPdfError(message)) {
      if (!environment.production) {
        console.warn('PDF loading cancelado (benigno, não reportado):', message);
      }
      return;
    }

    console.error('PDF loading failed:', message);
    this.loadError =
      'Não foi possível carregar o PDF. Feche e abra o arquivo novamente.';
    this.cdr.markForCheck();

    Sentry.captureMessage('PDF loading failed', {
      level: 'error',
      extra: {
        message: event?.message,
        filename: this.config?.filename
      }
    });
  }

  private setupPdfErrorHandler(): void {
    // Capturar erros do PDF.js, especialmente do OverlayManager e WorkerTransport
    if (typeof window === 'undefined') {
      return;
    }

    // Remover listener anterior se existir
    if (this.errorHandlerBound) {
      window.removeEventListener('unhandledrejection', this.errorHandlerBound);
    }

    // Criar handler bound para poder remover depois
    this.errorHandlerBound = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      if (!error || typeof error !== 'object' || !('message' in error)) {
        return;
      }

      const errorMessage = (error as { message?: string }).message || '';

      // Cancelamentos benignos do pdf.js (documento/worker destruído com
      // operação pendente). Sempre suprimir — não são falhas reais e o
      // "Transport destroyed"/"Invalid page request" só aparecem no teardown.
      if (isBenignPdfError(errorMessage)) {
        event.preventDefault();
        if (!environment.production) {
          console.warn('PDF.js cancelamento benigno (suprimido):', errorMessage);
        }
        return;
      }

      // Verificar se é um erro de WorkerTransport/messageHandler (ex: durante impressão)
      if (errorMessage.includes('sendWithPromise') ||
          errorMessage.includes('messageHandler') ||
          errorMessage.includes('WorkerTransport')) {

        // Suprimir se cleanup está em progresso (erro esperado)
        if (this.cleanupInProgress) {
          if (!environment.production) {
            console.warn('PDF.js worker error durante cleanup (suprimido):', errorMessage);
          }
          event.preventDefault();
          return;
        }

        // Se não está em cleanup, é um erro real - deixar propagar para Sentry
        if (!environment.production) {
          console.error('PDF.js worker error (não esperado):', errorMessage);
        }
        return;
      }

      // Verificar se é um erro do OverlayManager
      if (errorMessage.toLowerCase().includes('overlay') ||
          errorMessage.includes('OverlayManager') ||
          errorMessage.includes('does not exist')) {

        // Sempre prevenir propagação deste erro para evitar que bloqueie a aplicação
        event.preventDefault();

        if (!environment.production) {
          console.warn('PDF.js OverlayManager error detected:', errorMessage);
        }

        // Se cleanup está em progresso, apenas ignorar
        if (this.cleanupInProgress) {
          return;
        }

        // Tentar recuperar apenas uma vez se o PDF ainda não carregou completamente
        if (!this.errorRetryAttempted && this.config && !this.pdfFullyLoaded) {
          if (!environment.production) {
            console.log('Attempting to recover from OverlayManager error...');
          }
          this.errorRetryAttempted = true;

          // Reinicializar o PDF com um delay maior
          setTimeout(() => {
            if (this.config && !this.cleanupInProgress) {
              this.initializePdfSource(this.config);
            }
          }, 300);
        } else if (!this.pdfFullyLoaded) {
          // Registrar no Sentry apenas se o PDF não carregou e retry falhou
          Sentry.captureMessage('PDF OverlayManager error', {
            level: 'warning',
            tags: {
              component: 'pdf-viewer',
              error_type: 'overlay_manager'
            },
            extra: {
              filename: this.config?.filename,
              viewerInitialized: this.viewerInitialized,
              documentLoaded: this.documentLoaded,
              pdfFullyLoaded: this.pdfFullyLoaded,
              errorMessage
            }
          });
        }
        // Se o PDF já carregou, ignorar silenciosamente - é um erro spurious
      }
    };

    window.addEventListener('unhandledrejection', this.errorHandlerBound);
  }

  hide(): void {
    // Marcar cleanup como em progresso ANTES de qualquer operação
    this.cleanupInProgress = true;

    // Limpar src imediatamente para remover componente via *ngIf
    this.src = null;
    this.showToolbar = false;

    // Limpar timeout se existir
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
      this.initializationTimeout = null;
    }

    // Limpar recursos antes de esconder
    if (this.currentObjectUrl) {
      try {
        URL.revokeObjectURL(this.currentObjectUrl);
      } catch (e) {
        console.warn('Error revoking object URL in hide():', e);
      }
      this.currentObjectUrl = null;
    }

    this.viewerInitialized = false;
    this.documentLoaded = false;
    this.pdfFullyLoaded = false;
    this.errorRetryAttempted = false;

    if (this.resolve) {
      this.resolve();
    }
    this.modal.hide();
  }

  /**
   * Com URL, o Blob para envio só existe com o PDF inteiro carregado (o pdf.js
   * lê por Range); antes disso o botão fica desabilitado.
   */
  get canSendToWA(): boolean {
    return typeof this.config?.src !== "string" || this.pdfFullyLoaded;
  }

  sendToWA(): void {
    this.actionError = null;
    // Com URL (pré-visualização lida do S3 por Range), o Blob vem do documento
    // já carregado no visualizador, como no download; antes o envio retornava
    // em silêncio para qualquer `src` string.
    const blob: Promise<Blob | undefined> =
      typeof this.config.src === "string"
        ? this.pdfViewerService.getCurrentDocumentAsBlob()
        : Promise.resolve(this.config.src);

    blob
      .then((pdf) => {
        if (!pdf) {
          throw new Error("PDF ainda não carregado");
        }
        return blobToBase64(pdf);
      })
      .then((base64String) => {
        const base64 = base64String.split(",")[1];

        this.waService.sendToWA(this.config.waNumbers || [], WAContentType.MEDIA, {
          mimetype: "application/pdf",
          filename: this.config.filename,
          value: base64,
          text: this.config.waText
        });
      })
      .catch((error) => {
        console.error("Erro:", error);
        this.actionError =
          "Não foi possível enviar o PDF. Feche e abra o arquivo novamente.";
        this.cdr.markForCheck();
      });
  }

  /**
   * Handler para o evento afterPrint do ngx-extended-pdf-viewer.
   * Chamado quando a impressão é concluída.
   */
  onAfterPrint(): void {
    if (this.cleanupInProgress) {
      return;
    }

    if (!environment.production) {
      console.log('PDF printed');
    }

    // Notificar callback de obtenção
    if (this.config?.onPdfObtained) {
      this.config.onPdfObtained('print');
    }
  }

  /**
   * Handler customizado para download do PDF.
   * Usa o serviço do ngx-extended-pdf-viewer para obter o blob e fazer download.
   */
  async onDownloadClick(): Promise<void> {
    if (this.cleanupInProgress || !this.pdfFullyLoaded) {
      return;
    }

    try {
      // Obter o blob do PDF atual
      const blob = await this.pdfViewerService.getCurrentDocumentAsBlob();

      if (!blob) {
        console.error('Não foi possível obter o blob do PDF');
        return;
      }

      // Criar link de download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.config?.filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revogar URL após download
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);

      if (!environment.production) {
        console.log('PDF downloaded');
      }

      // Notificar callback de obtenção
      if (this.config?.onPdfObtained) {
        this.config.onPdfObtained('download');
      }
    } catch (error) {
      console.error('Erro ao fazer download do PDF:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'pdf-viewer',
          action: 'download'
        },
        extra: {
          filename: this.config?.filename
        }
      });
    }
  }
}
